import { Box, Skeleton, Theme, Typography } from "@outschool/backpack";
import { SavedSearchFilters } from "@outschool/gql-backend-generated";
import {
  SearchFilters,
  SearchUseCase,
} from "@outschool/gql-frontend-generated";
import { BASE_LOCALE, useLocale } from "@outschool/localization";
import { searchPath, toOnlineClassesSlugID } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  useEsaExperience,
  useEsaWaitPeriod,
  useFundingProgramAbbr,
  useSession,
} from "@outschool/ui-auth";
import { LazyBox } from "@outschool/ui-components-shared";
import {
  CategoryPageContentfulBanner,
  CategoryPageVisitHistory,
  MagnetPagesCarousel,
  OnboardingSnackBar,
  TutorMatchEnrollmentDataContext,
  TutorMatchEnrollmentDataType,
  useMagnetPageQuery,
  useTutoringCLPFiltersExperiment,
  useTutoringCLPFiltersMobileExperiment,
} from "@outschool/ui-components-website";
import {
  Loading,
  Space,
  TrackingPage,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import {
  pxToRem,
  responsive,
  simpleRandomId,
  useIsMobile,
  useNodeRef,
} from "@outschool/ui-utils";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { useDebouncedCallback as useDebouncedCallbackVendor } from "use-debounce";

import HeroImageJpg from "../../../../images/hero_image_bclp.jpg";
import * as Env from "../../../../shared/Env";
import * as Routes from "../../../../shared/Routes";
import { useCategoryPageQuery } from "../../../hooks/useCategoryPageQuery";
import { duplicateError } from "../../../lib/duplicateError";
import {
  SeoCategory,
  isSeoCategory,
  seoWhyAttendBlurb,
  seoWhyAttendTitle,
} from "../../../lib/seo";
import { useGetSeoBreadcrumbTrailScript } from "../../../lib/seo/hooks/useGetSeoBreadcrumbTrailScript";
import TutoringEnrollmentModal from "../../../routes/TutoringEnrollmentModal";
import { EnrollmentFromStripePurchaseOrder } from "../../enrolling/EnrollmentMeetingPurchaseWithStripe";
import HeaderTags from "../../HeaderTags";
import IterableEmailCaptureContentfulBox from "../../IterableEmailCaptureContentful";
import SimpleLayout, { SimpleLayoutProps } from "../../layouts/SimpleLayout";
import VisitHistoryListingsCarousel from "../../listings/carousels/VisitHistoryListingsCarousel";
import { uniqueIdFromFilters } from "../../search/common";
import { DefaultErrorBoundaryMessage } from "../../SimpleErrorBoundary";
import CategoryBreadcrumbs from "../CategoryBreadcrumbs";
import CategoryPills from "../CategoryPills";
import CuratedReviews from "../curated/CuratedReviews";
import { CategoryListingsFilters } from "../filters/CategoryListingsFilters";
import { HeroFilters } from "../filters/HeroFilters";
import { HeroFiltersButtonMobile } from "../filters/HeroFiltersButtonMobile";
import { useListingsData } from "../hooks/useListingsData";
import TutorMatchFilters from "../TutorMatchFilters";
import { BasicHeroSectionTreatment } from "./BasicHeroSectionTreatment";
import CategoryListings from "./CategoryListings";

export default function CategoryPage() {
  const location = useLocation();
  const locale = useLocale();
  const isEnglishPage: boolean = locale === BASE_LOCALE;
  const esaWaitPeriod = useEsaWaitPeriod();
  const pageImpressionId = React.useMemo(() => simpleRandomId(), []);
  const history = useHistory();
  const categorySlug = toOnlineClassesSlugID(
    useParams<{ themeOrQuery?: string }>()?.themeOrQuery || "",
  );
  const isMobile = useIsMobile();
  const trackEvent = useTrackEvent();
  const { isLoggedIn, currentUser } = useSession();

  const {
    loading: loadingCategory,
    data,
    error,
  } = useCategoryPageQuery(categorySlug);
  const categoryPage = data?.categoryPage;

  const { loading: magnetLoading, articles: magnetArticles } =
    useMagnetPageQuery({ categoryLabel: categoryPage?.slug });

  // Apply this order of precedence:
  // 1. ESA State
  // 2. Session State Funding Program
  // 3. Self Elected State Funding Program
  const esaState = useFundingProgramAbbr();
  const { sessionStateFundingProgram } = useEsaExperience();
  const selfElectedStateFundingProgram =
    currentUser?.selfElectedStateFundingProgram;
  const fundingProgramStateAbbreviation =
    esaState ||
    sessionStateFundingProgram?.stateAbbreviation ||
    selfElectedStateFundingProgram?.stateAbbreviation;
  const predefinedFilters = !!fundingProgramStateAbbreviation
    ? ({
        ...categoryPage?.searchFilters,
        startAfter: dayjs().add(esaWaitPeriod, "days").format("YYYY-MM-DD"),
        fundingPrograms: [fundingProgramStateAbbreviation],
      } as SavedSearchFilters)
    : categoryPage?.searchFilters;

  const {
    error: listingError,
    exactTotal,
    fetchMore,
    isFetchingMore,
    listings,
    loading: loadingListings,
    searchFilters,
    updateSearchFilters,
    listingsSearchUid: searchUid,
  } = useListingsData({
    // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    categorySlug,
    skip: loadingCategory,
    // @ts-ignore TS(2322): Type 'Maybe<SavedSearchFilters> | undefined' is no... Remove this comment to see the full error message
    predefinedCategoryFilters: predefinedFilters,
    pageLocation: "BASIC_CATEGORY",
    useCase: SearchUseCase.CategoryContent,
  });

  const databaseSearchFilters = categoryPage?.searchFilters;

  const [tutorMatchEnrollmentData, setTutorMatchEnrollmentData] =
    React.useState<TutorMatchEnrollmentDataType | undefined>(undefined);
  const [tutorMatchActivityEnrollNode, tutorMatchActivityEnrollRef] =
    useNodeRef();
  const { trackTouch: trackTutorMatchEnroll } = useImpressionTracking({
    node: tutorMatchActivityEnrollNode,
    trackingLabel: "tutor_match_page_activity_enroll",
    sharedProperties: {
      activity_uid: tutorMatchEnrollmentData?.activity.uid,
      sectionUid: tutorMatchEnrollmentData?.section?.uid,
    },
    uniqueId: `tutor_match_page_activity_enroll-${tutorMatchEnrollmentData?.activity.uid}`,
  });
  const [tutorMatchActivityEnrollmentOnComplete] = useDebouncedCallbackVendor(
    async (enrollments: EnrollmentFromStripePurchaseOrder[]) => {
      if (tutorMatchEnrollmentData) {
        const enrollment = enrollments[0];
        if (enrollment) {
          // track learner planner enrollment
          trackTutorMatchEnroll({
            enrollmentUid: enrollment.uid,
            activityUid: tutorMatchEnrollmentData.activity.uid,
            sectionUid: enrollment.section_uid,
          });
        }

        enrollment &&
          setTutorMatchEnrollmentData?.({
            ...tutorMatchEnrollmentData,
            section: { uid: enrollment?.section_uid! },
          });

        return Promise.resolve();
      }
    },
    500,
  );

  useEffect(() => {
    if (!loadingCategory && categoryPage === null) {
      trackEvent("category page client redirect to search", { categorySlug });
      // @ts-ignore TS(2532): Object is possibly 'undefined'.
      history.replace(searchPath({ q: categorySlug.replace(/-/g, " ") }));
    }
  }, [loadingCategory, categoryPage, categorySlug, trackEvent, history]);

  const isTutoringCLP = useMemo(() => {
    return searchFilters.capacityMax === 1 || categorySlug?.includes("tutor");
  }, [searchFilters.capacityMax, categorySlug]);

  const isTutoringCLPFilters = useTutoringCLPFiltersExperiment();
  const isTutoringCLPFiltersMobile = useTutoringCLPFiltersMobileExperiment();
  const useTutorMatchVariant =
    isTutoringCLP && (isTutoringCLPFilters || isTutoringCLPFiltersMobile);
  const [showPersonalizeButton, setShowPeronalizeButton] = useState(true);

  useEffect(() => {
    if (!categoryPage) {
      return;
    }

    CategoryPageVisitHistory.pushToTop(categoryPage.slug);
  }, [categoryPage, error]);

  const seoBreadcrumbTrailScript = useGetSeoBreadcrumbTrailScript(categoryPage);
  const metatags = React.useMemo(() => {
    return [
      { property: "og:title", content: categoryPage?.title },
      { name: "description", content: categoryPage?.description },
      {
        property: "og:description",
        content: categoryPage?.description,
      },
    ];
  }, [categoryPage]);

  if (error) {
    /*
     * Errors are deduplicated by Sentry, which will clobber any tags we
     * apply. To make sure that the tags are included, we'll wrap the
     * original error.
     */
    const wrappedError = duplicateError(
      error,
      // eslint-disable-next-line i18next/no-literal-string
      "Oops page displayed for error: ",
    );
    OsPlatform.captureError(wrappedError, {
      tags: {
        oops_page_displayed: true,
      },
    });
    return (
      <SimpleLayout>
        <DefaultErrorBoundaryMessage error={error} />
      </SimpleLayout>
    );
  }
  if (
    // if loading and !data render full-page loader
    (loadingCategory && !categoryPage) ||
    // if category does not exist resolver will return null
    // render full-page loader while waiting for redirect to search
    (!loadingCategory && categoryPage === null)
  ) {
    return <Loading fullHeight={true} />;
  }

  const layoutProps: Partial<SimpleLayoutProps> = {
    allowCategoriesNavbar: true,
  };
  return (
    <SimpleLayout hideReviewReminder {...layoutProps}>
      {loadingCategory ? (
        <Loading fullHeight={true} />
      ) : (
        <TutorMatchEnrollmentDataContext.Provider
          value={{ tutorMatchEnrollmentData, setTutorMatchEnrollmentData }}
        >
          <HeaderTags
            // @ts-ignore TS(2532): Object is possibly 'undefined'.
            title={categoryPage.title}
            // @ts-ignore TS(2322): Type '({ property: string; content: Maybe<string> ... Remove this comment to see the full error message
            meta={metatags}
            canonical={Routes.categoryPageUrl(
              // @ts-ignore TS(2532): Object is possibly 'undefined'.
              categoryPage.slug,
              // @ts-ignore TS(2532): Object is possibly 'undefined'.
              categoryPage.basePath,
            )}
            script={seoBreadcrumbTrailScript}
          />
          <TrackingPage
            name="categoryPage"
            uniqueId={uniqueIdFromFilters("CategoryPage", searchFilters)}
            filters={searchFilters}
            pageImpressionIdDefined={pageImpressionId}
            uniqueProperties={{
              categorySlug: categorySlug,
              searchResultsTotal: exactTotal,
            }}
          >
            {setImpressionNode => (
              <>
                <Box ref={(node: $TSFixMe) => setImpressionNode(node)}>
                  {isMobile &&
                    showPersonalizeButton &&
                    useTutorMatchVariant && (
                      <HeroFiltersButtonMobile
                        category={categorySlug}
                        updateSearchFilters={updateSearchFilters}
                        searchFilters={searchFilters}
                        setShowButton={setShowPeronalizeButton}
                      />
                    )}
                  <BasicHeroSectionTreatment
                    // @ts-ignore TS(2532): Object is possibly 'undefined'.
                    title={categoryPage.title}
                    imageUrl={HeroImageJpg}
                    isTutoringCLP={useTutorMatchVariant}
                    Breadcrumbs={
                      <CategoryBreadcrumbs
                        treatment
                        // @ts-ignore TS(2322): Type 'Maybe<Maybe<CategoryPage>[]>' is not assigna... Remove this comment to see the full error message
                        ancestors={categoryPage.ancestors}
                        currentCategoryName={
                          // @ts-ignore TS(2532): Object is possibly 'undefined'.
                          categoryPage.nameTitleCased
                        }
                        // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                        categorySlug={categorySlug}
                        pathname={location.pathname}
                      />
                    }
                    HeroFilters={
                      <HeroFilters
                        categorySlug={categorySlug}
                        updateSearchFilters={updateSearchFilters}
                        searchFilters={searchFilters}
                      />
                    }
                  />
                </Box>
                {!useTutorMatchVariant && <Space y="medium" />}
                <CategoryPageContentfulBanner />
                {!useTutorMatchVariant && <Space y="medium" />}
                {useTutorMatchVariant && (
                  <Box sx={{ width: "100% !important" }}>
                    <TutorMatchFilters
                      filters={searchFilters}
                      categorySetFilters={updateSearchFilters}
                      showTheme={false}
                    />
                  </Box>
                )}
                <CategoryListings
                  isReadOnlyMode={Env.IS_READ_ONLY_MODE}
                  ListingFilters={
                    !useTutorMatchVariant ? (
                      <CategoryListingsFilters
                        updateListings={updateSearchFilters}
                        searchFilters={searchFilters}
                        // @ts-ignore TS(2322): Type 'Maybe<SavedSearchFilters> | undefined' is no... Remove this comment to see the full error message
                        preSetFilters={databaseSearchFilters}
                        resetSearchFilters={() => {
                          // @ts-ignore TS(2345): Argument of type 'Maybe<SavedSearchFilters> | unde... Remove this comment to see the full error message
                          updateSearchFilters(databaseSearchFilters);
                        }}
                      />
                    ) : (
                      <></>
                    )
                  }
                  ListingEmbeddings={[
                    {
                      positionFn: index =>
                        // @ts-ignore TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                        isSeoCategory(categorySlug) &&
                        (index === 4 ||
                          // or if the number of listings returned is less than desired index
                          // display after the last listing
                          (listings.length <= 4 &&
                            index === listings.length - 1)),
                      Component: (
                        <CategorySeoBlurb
                          categorySlug={categorySlug as SeoCategory}
                        />
                      ),
                    },
                  ]}
                  // @ts-ignore TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
                  categorySlug={categorySlug}
                  error={listingError}
                  exactTotal={exactTotal}
                  fetchMore={fetchMore}
                  isFetchingMore={isFetchingMore}
                  listings={listings}
                  loading={loadingCategory || loadingListings}
                  searchUid={searchUid}
                  variant={"category-basic"}
                  pageLocation="BASIC_CATEGORY"
                  isTutoringCLP={isTutoringCLP}
                />
                {!isTutoringCLP && (
                  <>
                    <Space y={responsive({ default: 10, small: 34 })} />
                    <IterableEmailCaptureContentfulBox
                      categorySlug={categorySlug}
                    />
                    {!isMobile && (
                      <LazyBox
                        placeholder={
                          <Skeleton
                            variant="rectangular"
                            height={"203px"}
                            sx={{ my: "1em", mx: ".5em", borderRadius: "10px" }}
                          />
                        }
                      >
                        <VisitHistoryListingsCarousel
                          carouselRowNum={0}
                          headingSx={{ textAlign: "left" }}
                        />
                      </LazyBox>
                    )}
                    <Space y={responsive({ default: 25 })} />
                    <CuratedReviews
                      // @ts-ignore TS(2322): Type 'CategoryPage | undefined' is not assignable ... Remove this comment to see the full error message
                      category={categoryPage}
                      filters={predefinedFilters as SearchFilters}
                    />
                    <Space y={responsive({ default: 78, small: 104 })} />
                  </>
                )}
                {/* @ts-ignore TS(2322): Type 'CategoryPage | undefined' is not assignable ... Remove this comment to see the full error message */}
                <CategoryPills category={categoryPage} />
                <Space y={responsive({ default: 78, small: 104 })} />
                {!isEnglishPage ? (
                  <></>
                ) : magnetLoading ? (
                  <Loading />
                ) : (
                  <>
                    <MagnetPagesCarousel
                      articles={magnetArticles}
                      carouselSx={{
                        maxWidth: "100%",
                      }}
                    />
                    <Space y={responsive({ default: 90, small: 69 })} />
                  </>
                )}
              </>
            )}
          </TrackingPage>
          {tutorMatchEnrollmentData && (
            <TutoringEnrollmentModal
              ref={tutorMatchActivityEnrollRef}
              tutorMatchEnrollmentData={tutorMatchEnrollmentData}
              onCancel={() => {
                setTutorMatchEnrollmentData(undefined);
              }}
              onPaymentComplete={tutorMatchActivityEnrollmentOnComplete}
            />
          )}
        </TutorMatchEnrollmentDataContext.Provider>
      )}
      {!isLoggedIn && isMobile && !isTutoringCLP && <OnboardingSnackBar />}
    </SimpleLayout>
  );
}

const CategorySeoBlurb: React.FC<{
  categorySlug: SeoCategory;
}> = ({ categorySlug }) => {
  return (
    <>
      <Box
        flex
        sx={(theme: Theme) => ({
          flexDirection: "column",
          alignItems: "center",
          background: theme.gradients?.primarySecondary,
          borderRadius: 0,

          [theme.breakpoints.up("md")]: {
            borderRadius: "1em",
          },
        })}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            paddingTop: pxToRem(48),
            paddingBottom: pxToRem(12),
            paddingX: "1em",

            [theme.breakpoints.up("md")]: {
              paddingX: "3em",
            },

            maxWidth: pxToRem(700),
          })}
        >
          <Typography variant="h3" emphasized>
            {seoWhyAttendTitle(categorySlug)}
          </Typography>

          <Box
            sx={{
              marginTop: pxToRem(20),
              marginBottom: pxToRem(28),
            }}
          >
            {seoWhyAttendBlurb(categorySlug)}
          </Box>
        </Box>
      </Box>
      <Space y="large" />
    </>
  );
};
